<template>
  <hypto-popup :title="title">
    <template v-slot:body>
      <div class="merge-fields">
        <md-field id="VerifyOptions" class="verify-options">
          <label>Verify</label>
          <md-select @md-opened="setDropdownPosOffset('VerifyOptions')"
                    v-model="verifyValue" :disabled="!manageAccess.bankVerify || !manageAccess.upiVerify">
            <md-option v-for="(option, index) in verifyOptions" :key="index"
                      :value="option.value">
              {{option.text}}
            </md-option>
          </md-select>
        </md-field>

        <div v-if="verifyValue === 'Bank'">
          <md-field :class="{'input-error': $v.accountNumber.$error || formErrors.number.length}">
            <label>Account Number</label>
            <md-input v-model="accountNumber" @blur="$v.accountNumber.$touch()"
                      :ref="'FirstField'" />
          </md-field>
          <h6 class="error-message" v-if="$v.accountNumber.$error && !$v.accountNumber.required">Please enter Account Number</h6>
          <h6 class="error-message" v-else-if="$v.accountNumber.$error && !$v.accountNumber.accountNumber">Account Number should contain 1 to 34 alphanumeric characters</h6>
          <error-info v-else :errorList="formErrors.number" />

          <md-field :class="{'input-error': $v.ifsc.$error || formErrors.ifsc.length}">
            <label>IFSC</label>
            <md-input v-model="ifsc" @blur="$v.ifsc.$touch()" maxlength="11"
                      class="upper-case" />
          </md-field>
          <ifsc-details :bankDetails="bankDetails" />
          <h6 class="error-message" v-if="$v.ifsc.$error && !$v.ifsc.required">Please enter IFSC Number</h6>
          <h6 class="error-message" v-else-if="$v.ifsc.$error && !$v.ifsc.validIfsc">Invalid IFSC Number. Please verify</h6>
          <error-info v-else :errorList="formErrors.ifsc" />
        </div>

        <div v-else>
          <md-field :class="{'input-error': $v.upiId.$error || formErrors.upi_id.length || trackUpi.invalid}">
            <label>UPI ID</label>
            <md-input v-model="upiId" @blur="touchUpi" :ref="'FirstField'" />
            <h6 class="error-message" v-if="$v.upiId.$error && !$v.upiId.required">Please enter UPI ID</h6>
            <h6 class="error-message" v-else-if="$v.upiId.$error">Please enter a valid UPI ID</h6>
            <h6 class="error-message" v-else-if="trackUpi.invalid">IFSC present in UPI ID is invalid</h6>
            <error-info v-else :errorList="formErrors.upi_id" />
          </md-field>
        </div>

        <md-field :class="{'input-error': $v.refNum.$error}">
          <label>Reference Number</label>&nbsp;
          <i>(Optional)</i>
          <md-input v-model="refNum" @keyup.enter="verify" @blur="$v.refNum.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.refNum.$error">Reference Number should contain only alphanumeric characters, hyphen, underscore or dot</h6>
        <error-info v-else :errorList="formErrors.reference_number" />
      </div>
    </template>

    <template v-slot:footer>
      <error-info :errorList="formErrors.transaction" />
      <button class="primary-btn align-r" @click="verify">Verify</button>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import ErrorInfo from '@/components/global/ErrorInfo'
import IfscDetails from '@/components/AccountSummary/IfscDetails'

import ApiServices from '@/services/api/transfers'
import { required } from 'vuelidate/lib/validators'
import { validIfsc, accountNumber, validVpa, refNum } from '@/utils/helpers/validators'
import { openPopup, checkFormErrors, clearFormErrors, setDropdownPosOffset } from '@/utils/helpers'
import { mapMutations } from 'vuex'

export default {
  name: 'Verifications',
  components: { HyptoPopup, ErrorInfo, IfscDetails },
  props: {
    manageAccess: Object
  },
  data () {
    return {
      title: 'Bank & UPI Verification',
      accountNumber: null,
      ifsc: null,
      bankDetails: {},
      upiId: null,
      refNum: null,
      verifyOptions: [
        {
          text: 'Bank Account',
          value: 'Bank'
        },
        {
          text: 'UPI ID',
          value: 'UPI'
        }
      ],
      verifyValue: 'Bank',
      inProgress: false,
      trackUpi: {
        invalid: false,
        changed: false
      },
      formErrors: {
        number: [],
        ifsc: [],
        reference_number: [],
        transaction: [],
        upi_id: []
      }
    }
  },
  validations () {
    let valid = {
      refNum: {
        refNum
      }
    }

    if (this.verifyValue === 'Bank') {
      valid = {
        ...valid,
        accountNumber: {
          required,
          accountNumber
        },
        ifsc: {
          required,
          validIfsc
        },
        bankDetails: {
          bank: {
            required
          },
          branch: {
            required
          }
        }
      }
    } else {
      valid = {
        ...valid,
        upiId: {
          required,
          validVpa
        }
      }
    }
    return valid
  },
  watch: {
    ifsc () {
      this.bankDetails = {}

      if (this.ifsc && this.ifsc.length === 11 && validIfsc(this.ifsc)) {
        this.ifscDetails()
      }

      this.formErrors.ifsc = []
      this.formErrors.transaction = []
    },
    accountNumber () {
      this.formErrors.number = []
      this.formErrors.transaction = []
    },
    refNum () {
      this.formErrors.reference_number = []
      this.formErrors.transaction = []
    },
    upiId () {
      this.trackUpi = {
        invalid: false,
        changed: true
      }
      this.formErrors.upi_id = []
      this.formErrors.transaction = []
    },
    verifyValue (newVal) {
      if (newVal === 'UPI') {
        this.accountNumber = null
        this.ifsc = null
        this.bankDetails = {}
      } else {
        this.upiId = null
      }
      this.refNum = null
      this.$v.$reset()
      this.clearFormErrors(this.formErrors)

      setTimeout(() => {
        this.$refs.FirstField.$el.focus()
      })
    }
  },
  created () {
    if (this.manageAccess.bankVerify) {
      this.verifyValue = 'Bank'
      this.title = this.manageAccess.upiVerify ? 'Bank & UPI Verification' : 'Bank Verification'
    } else {
      this.verifyValue = 'UPI'
      this.title = 'UPI Verification'
    }
  },
  mounted () {
    this.$refs.FirstField.$el.focus()
  },
  methods: {
    openPopup,
    checkFormErrors,
    clearFormErrors,
    setDropdownPosOffset,
    ...mapMutations(['SET_BANK_TRANSFERRED']),
    verify () {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors) && !this.inProgress && !this.trackUpi.invalid) {
        this.inProgress = true

        if (this.verifyValue === 'Bank') {
          let params = {
            number: this.accountNumber,
            ifsc: this.ifsc.toUpperCase(),
            reference_number: this.refNum
          }

          this.proceed('bankVerify', params)
        } else {
          let params = {
            upi_id: this.upiId,
            reference_number: this.refNum
          }

          this.proceed('upiVerify', params)
        }
      }
    },
    proceed (api, params) {
      ApiServices[api](params).then(response => {
        this.handleResponse(response)
      }).catch(error => {
        this.handleErrorResponse(error)
      })
    },
    handleResponse (response) {
      if (response.success) {
        this.successToastMsg(response.message)
        this.openPopup(null)
        this.SET_BANK_TRANSFERRED()
      } else {
        this.errorToastMsg(response.message)
      }
    },
    handleErrorResponse (error) {
      this.inProgress = false
      this.formErrors = {
        ...this.formErrors,
        ...error.body.reason
      }
    },
    ifscDetails () {
      if (!this.$v.ifsc.$error) {
        ApiServices.ifscDetails(this.ifsc).then(response => {
          this.bankDetails = response.data
        }).catch(error => {
          this.formErrors.ifsc = [error.body.message]
        })
      }
    },
    touchUpi () {
      this.$v.upiId.$touch()
      if (!this.$v.upiId.$error && this.upiId && this.upiId.includes('.ifsc') && this.trackUpi.changed) {
        const pos1 = this.upiId.indexOf('@') + 1
        const pos2 = this.upiId.indexOf('.ifsc')
        const ifsc = this.upiId.substring(pos1, pos2)

        ApiServices.ifscDetails(ifsc).then(response => {
          this.trackUpi = {
            invalid: false,
            changed: false
          }
        }).catch(() => {
          this.trackUpi = {
            invalid: true,
            changed: false
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/mixins.scss';

  .upper-case {
    text-transform: uppercase;
  }

  .verify-options {
    margin-bottom: 15px !important;
  }

  i {
    color: $grey-light;
  }
</style>
